import { useCurrentUser } from '@hooks/useCurrentUser';
import usePersistentState from '@hooks/usePersistentState';
import { useEffect } from 'react';

import styles from './ReceiveGrid.module.css';

/**
 * What every receipt screen shares - the RM/PM receive grid (sites, stores, the factory portal)
 * and the finished-goods GRN - so the two read as one form, not two products.
 */

const ENTERED_BY_KEY = 'cm-receive-entered-by';

/**
 * Field labels on the design-system scale.
 *
 * Mantine's own defaults run a step larger than the rest of the app, which reads as a different
 * page rather than a different screen of the same one. Declared once because every header field
 * is the same kind of field.
 */
export const receiveFieldClasses = {
    label: styles.fieldLabel,
    description: styles.fieldDescription,
    // Same control language as the grid below, so the screen does not mix two input sizes.
    input: styles.cellInput,
};

/**
 * Who is receiving: STARTS AS WHOEVER IS SIGNED IN, stays whatever they make it. Nine times in
 * ten that is the account holder - so the name is seeded once, on first use, and never fought
 * over: a value someone typed (or a seed from a previous visit, kept by usePersistentState) is
 * left alone, and editing it to somebody else's name sticks. Falls back to the email's local
 * part for a login with no name. One remembered name across every receipt screen.
 */
export function useEnteredByName(): [string, (value: string) => void] {
    const [enteredBy, setEnteredBy] = usePersistentState<string>(ENTERED_BY_KEY, '');
    const { currentUser } = useCurrentUser();
    useEffect(() => {
        if (enteredBy.trim() || !currentUser) return;
        const name = [currentUser.firstName, currentUser.lastName]
            .filter(Boolean)
            .join(' ')
            .trim()
            || currentUser.email?.split('@')[0]
            || '';
        if (name) setEnteredBy(name);
        // Seed once when the user arrives; enteredBy is deliberately not a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);
    return [enteredBy, setEnteredBy];
}
